import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { CalendarClock, CircleDollarSign, FileCheck, Info, Percent, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDeData } from '@/componentes/CampoDeData';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { fileteDasAbas } from '@/componentes/filete';
import { Janela } from '@/componentes/JanelaDeAcao';
import { PaginaSemPermissao } from '@/componentes/SemPermissao';
import { RodapeDeContagem, Tabela, TextoDaCelula } from '@/componentes/Tabela';
import { diaDaOperacao } from '@/lib/dia-da-operacao';
import { formatarCompetencia, formatarDecimal, formatarDia, formatarInteiro, formatarReais, interpretarNumero } from '@/lib/formato';
import { motivoValido } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { perfilAPedir } from '@/lib/perfil-da-licenca';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDaRenovacao, type Criterio, type Proposta, type SituacaoDaProposta } from './api';
import { JanelaDaProposta } from './JanelaDaProposta';
import {
  codigoDaProposta,
  horas,
  NOME_DO_RISCO,
  detalheDaDecisao,
  tituloDaDecisao,
  detalheDoOrcamento,
  validarBuscaDosInvestimentos,
  type AbaDosInvestimentos,
} from './renovacao';
import { SeloDaProposta } from './Selo';
import { SeloDoOrcamento } from '@/modulos/orcamento/Selo';

const rota = getRouteApi('/investimentos');

const ABAS: readonly (readonly [AbaDosInvestimentos, string])[] = [
  ['plano', 'Plano e caixa'],
  ['propostas', 'Propostas'],
  ['criterios', 'Critérios'],
];

type Acao = { tipo: 'propor' } | { tipo: 'criterio' } | { tipo: 'nota'; proposta: Proposta } | { tipo: 'decidir'; proposta: Proposta } | { tipo: 'orcamento'; proposta: Proposta } | { tipo: 'aprovarCriterio'; criterio: Criterio } | { tipo: 'retirarCriterio'; criterio: Criterio } | null;

/**
 * MÓDULO 20 — CUSTOS › PLANO DE INVESTIMENTOS (ferramenta 4 do v6; 0107): a
 * ordem das propostas pelas notas dos critérios aprovados, o corte do caixa
 * do orçamento (o menor saldo projetado do 19), a capacidade por tipo contra a
 * demanda do cenário, as propostas com a decisão e os critérios.
 *
 * Quem propõe não aprova (o banco recusa); a tela mostra a alçada de quem
 * está logado antes do botão de decidir (p.44). "Levar ao orçamento" cria a
 * linha numa versão em rascunho do 19 — nunca no aprovado.
 */
export function PaginaInvestimentos() {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = permissoes.includes('renovacao.ler');
  const propoe = permissoes.includes('investimento.propor');
  const aprova = permissoes.includes('investimento.aprovar');
  const aprovaCriterio = permissoes.includes('criterio.aprovar');
  const levaAoOrcamento = propoe && permissoes.includes('orcamento.gravar');
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const aba: AbaDosInvestimentos = busca.aba ?? 'plano';
  const [acao, setAcao] = useState<Acao>(null);

  if (sessao.data && !pode) {
    return <PaginaSemPermissao sobretitulo="Custos" titulo="Plano de investimentos" modulo="o plano de investimentos" licenca="renovacao.ler" />;
  }
  const fechar = () => { setAcao(null); };

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Custos"
          titulo="Plano de investimentos"
          subtitulo="As propostas na ordem dos critérios aprovados, cortadas pelo caixa do orçamento"
          acoes={
            propoe ? (
              aba === 'criterios'
                ? <Botao nivel="primario" onClick={() => { setAcao({ tipo: 'criterio' }); }}>Propor critério</Botao>
                : <Botao nivel="primario" onClick={() => { setAcao({ tipo: 'propor' }); }}>Propor investimento</Botao>
            ) : null
          }
        />
        <div role="tablist" aria-label="Seções do plano de investimentos" ref={fileteDasAbas} className="barra-de-abas filete-das-abas">
          {ABAS.map(([id, rotulo]) => (
            <button key={id} type="button" role="tab" aria-selected={aba === id} className="aba" onClick={() => { void navegar({ search: validarBuscaDosInvestimentos({ aba: id }) }); }}>
              {rotulo}
            </button>
          ))}
        </div>
        {aba === 'plano' ? <AbaDoPlano /> : null}
        {aba === 'propostas' ? <AbaDasPropostas aoAgir={setAcao} /> : null}
        {aba === 'criterios' ? <AbaDosCriterios aoAgir={setAcao} /> : null}
      </div>
      {propoe ? <JanelaDaProposta aberta={acao?.tipo === 'propor'} aoFechar={fechar} /> : null}
      {propoe ? <JanelaDoCriterio aberta={acao?.tipo === 'criterio'} aoFechar={fechar} /> : null}
      {propoe && acao?.tipo === 'nota' ? <JanelaDaNota proposta={acao.proposta} aoFechar={fechar} /> : null}
      {aprova && acao?.tipo === 'decidir' ? <JanelaDaDecisao proposta={acao.proposta} aoFechar={fechar} /> : null}
      {levaAoOrcamento && acao?.tipo === 'orcamento' ? <JanelaDoOrcamento proposta={acao.proposta} aoFechar={fechar} /> : null}
      {aprovaCriterio && acao?.tipo === 'aprovarCriterio' ? <JanelaDaAprovacaoDoCriterio criterio={acao.criterio} aoFechar={fechar} /> : null}
      {aprovaCriterio && acao?.tipo === 'retirarCriterio' ? <JanelaDaRetirada criterio={acao.criterio} aoFechar={fechar} /> : null}
    </div>
  );
}

function valorDaProposta(p: Pick<Proposta, 'investimento' | 'compromissoMensal' | 'mesesDeCompromisso'>): string {
  if (p.investimento !== null) return formatarReais(p.investimento);
  if (p.compromissoMensal !== null) return `${formatarReais(p.compromissoMensal)}/mês · ${String(p.mesesDeCompromisso ?? 0)} meses`;
  return '—';
}

function AbaDoPlano() {
  const consulta = useQuery({ queryKey: ['renovacao', 'plano'], queryFn: apiDaRenovacao.plano, meta: { oQueLia: 'o plano de investimentos' } });
  const p = consulta.data;
  const cabem = p?.ranking.filter((l) => l.cabe === true).length ?? 0;
  if (consulta.isError && !p) return <ErroDaLista oQue="As propostas do plano" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;
  return (
    <>
      <div className="bloco-rebaixado flex items-start gap-3 rounded-controle p-4">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-informacao" />
        <p className="txt-corpo text-pretty text-tinta">
          {p?.orcamento ? `O caixa vem do orçamento de ${String(p.exercicio)} (${p.orcamento.versao.nome}, versão ${String(p.orcamento.versao.versao)}${p.orcamento.cenario ? `, cenário ${p.orcamento.cenario.nome}` : ''}). ${p.orcamento.frase}` : 'Sem orçamento aprovado do exercício: a demanda por tipo não entra, e o corte usa só o saldo informado no caixa.'}
        </p>
      </div>
      <FaixaIndicadores>
        <CardIndicador rotulo="Menor saldo projetado" icone={TriangleAlert} tom="parada" carregando={!p} valor={p?.caixa ? formatarReais(p.caixa.valor) : p ? 'Sem caixa' : ''} alerta={(p?.caixa?.valor ?? 0) < 0} contexto={p?.caixa ? `Em ${formatarDia(p.caixa.data)}${p.caixa.saldoInformado ? '' : ' · sem saldo informado'}` : 'Do orçamento e caixa'} />
        <CardIndicador rotulo="Capacidade de caixa" icone={CircleDollarSign} tom="neutro" carregando={!p} valor={p ? (p.caixa?.capacidade == null ? 'Não conhecida' : formatarReais(p.caixa.capacidade)) : ''} contexto="O que cabe antes do menor saldo" />
        <CardIndicador rotulo="Propostas que cabem" icone={FileCheck} tom="operando" carregando={!p} valor={p ? `${formatarInteiro(cabem)} de ${formatarInteiro(p.ranking.length)}` : ''} contexto="Na ordem dos critérios" />
        <CardIndicador rotulo="Critérios vigentes" icone={Percent} tom="marca" carregando={!p} valor={p ? formatarInteiro(p.criteriosVigentes) : ''} contexto="Só o aprovado entra na nota" />
      </FaixaIndicadores>
      <h2 className="txt-secao text-tinta">Em que ordem investir, e o que cabe no caixa?</h2>
      <Tabela
        rotulo="A ordem das propostas"
        colunas={[
          { chave: 'posicao', rotulo: '#', alinhamento: 'numero', celula: (l) => <span className="numero text-tinta">{String(l.posicao)}</span> },
          {
            chave: 'proposta', rotulo: 'Proposta', prioridade: 'chave', celula: (l) => (
              <span className="flex min-w-0 flex-col">
                <span className="codigo text-tinta">{codigoDaProposta(l.proposta.numero)}</span>
                <TextoDaCelula texto={`${l.proposta.nomeDoTipo} · ${l.proposta.codigo ?? l.proposta.tipoDeMaquina ?? ''} · ${l.proposta.descricao}`} classe="txt-apoio text-tinta-apagada" linhas={2} />
              </span>
            ),
          },
          { chave: 'pontuacao', rotulo: 'Nota', alinhamento: 'numero', celula: (l) => <span className="numero text-tinta">{l.pontuacao === null ? (l.semNota.length ? 'falta nota' : '—') : formatarDecimal(l.pontuacao, 2)}</span> },
          { chave: 'consumo', rotulo: 'Tira do caixa', alinhamento: 'numero', celula: (l) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(l.consumo)}</span> },
          { chave: 'quando', rotulo: 'Mês', prioridade: 'secundaria', celula: (l) => <span className="numero text-tinta">{formatarCompetencia(l.proposta.dataPretendida)}</span> },
          {
            chave: 'cabe', rotulo: 'Caixa', celula: (l) => (
              <span className="flex flex-col items-start gap-1">
                {l.cabe === null ? <SeloDoOrcamento rotulo="Sem corte" tom="neutro" /> : l.cabe ? <SeloDoOrcamento rotulo="Cabe" tom="sucesso" /> : <SeloDoOrcamento rotulo="Não cabe" tom="erro" />}
                <TextoDaCelula texto={l.motivo} classe="txt-apoio text-tinta-apagada" linhas={2} />
              </span>
            ),
          },
          { chave: 'situacao', rotulo: 'Situação', celula: (l) => <SeloDaProposta situacao={l.proposta.situacao} /> },
        ]}
        linhas={p?.ranking ?? []}
        chaveDaLinha={(l) => l.id}
        carregando={consulta.isPending}
        alturaDupla
        erro={consulta.isError ? <ErroDaLista oQue="As propostas do plano" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
        vazio={<EstadoVazio icone={FileCheck} titulo="Nenhuma proposta de investimento" descricao="Proponha o investimento a partir de um estudo concluído, ou um adicional por tipo de máquina." />}
        rodape={<RodapeDeContagem total={p?.ranking.length ?? 0} />}
      />
      <h2 className="txt-secao text-tinta">Falta máquina de algum tipo?</h2>
      <Tabela
        rotulo="A capacidade por tipo contra a demanda do orçamento"
        colunas={[
          { chave: 'tipo', rotulo: 'Tipo', prioridade: 'chave', celula: (t) => <span className="txt-corpo text-tinta">{t.tipo}</span> },
          { chave: 'maquinas', rotulo: 'Máquinas', alinhamento: 'numero', celula: (t) => <span className="numero text-tinta">{formatarInteiro(t.maquinas)}</span> },
          { chave: 'capacidade', rotulo: 'Capacidade', alinhamento: 'numero', celula: (t) => <span className="numero whitespace-nowrap text-tinta">{t.capacidadeHorasMes === null ? '—' : `${horas(t.capacidadeHorasMes)}/mês`}</span> },
          { chave: 'demanda', rotulo: 'Demanda', alinhamento: 'numero', celula: (t) => <span className="numero whitespace-nowrap text-tinta">{t.demandaHorasMes === null ? 'sem demanda' : `${horas(t.demandaHorasMes)}/mês`}</span> },
          { chave: 'falta', rotulo: 'Falta', alinhamento: 'numero', celula: (t) => <span className={`numero whitespace-nowrap ${(t.falta ?? 0) > 0 ? 'text-parada-texto' : 'text-tinta'}`}>{t.falta === null ? '—' : `${horas(t.falta)}/mês`}</span> },
        ]}
        linhas={p?.capacidadePorTipo ?? []}
        chaveDaLinha={(t) => t.tipo}
        carregando={consulta.isPending}
        erro={consulta.isError ? <ErroDaLista oQue="As capacidades por tipo" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
        vazio={<EstadoVazio titulo="Nenhum tipo de máquina na frota" descricao="Cadastre o modelo de cada máquina: a capacidade é por tipo." />}
        rodape={<RodapeDeContagem total={p?.capacidadePorTipo.length ?? 0} />}
      />
      {p && p.naoInclui.length > 0 ? (
        <div className="cartao flex flex-col gap-2 p-4">
          <h3 className="txt-corpo font-semibold text-tinta">O que ficou de fora</h3>
          <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">{p.naoInclui.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      ) : null}
    </>
  );
}

function AbaDasPropostas({ aoAgir }: { readonly aoAgir: (a: Acao) => void }) {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const propoe = permissoes.includes('investimento.propor');
  const levaAoOrcamento = propoe && permissoes.includes('orcamento.gravar');
  const consulta = useQuery({ queryKey: ['renovacao', 'propostas'], queryFn: apiDaRenovacao.propostas, meta: { oQueLia: 'as propostas de investimento' } });
  const d = consulta.data;
  return (
    <>
      {d && !d.temLicencaDeAprovar ? (
        <p className="txt-apoio text-tinta-secundaria">{`Decidir a proposta pede a licença de aprovar investimentos: hoje ela é do perfil ${perfilAPedir('investimento.aprovar')}.`}</p>
      ) : null}
      <Tabela
        rotulo="As propostas de investimento"
        colunas={[
          {
            chave: 'proposta', rotulo: 'Proposta', prioridade: 'chave', celula: (p) => (
              <span className="flex min-w-0 flex-col">
                <span className="codigo text-tinta">{codigoDaProposta(p.numero)}</span>
                <TextoDaCelula texto={`${p.codigo ?? p.tipoDeMaquina ?? ''} · ${p.descricao}`} classe="txt-apoio text-tinta-apagada" linhas={2} />
              </span>
            ),
          },
          { chave: 'tipo', rotulo: 'Tipo', prioridade: 'secundaria', celula: (p) => <span className="txt-corpo text-tinta">{p.nomeDoTipo}</span> },
          { chave: 'valor', rotulo: 'Valor', alinhamento: 'numero', celula: (p) => <span className="numero whitespace-nowrap text-tinta">{valorDaProposta(p)}</span> },
          { chave: 'mes', rotulo: 'Mês', celula: (p) => <span className="numero text-tinta">{formatarCompetencia(p.dataPretendida)}</span> },
          { chave: 'risco', rotulo: 'Risco', prioridade: 'secundaria', celula: (p) => <span className="txt-corpo text-tinta">{NOME_DO_RISCO[p.risco]}</span> },
          { chave: 'nota', rotulo: 'Nota', alinhamento: 'numero', celula: (p) => <span className="numero text-tinta">{p.pontuacao === null ? (p.semNota.length ? `falta ${String(p.semNota.length)}` : '—') : formatarDecimal(p.pontuacao, 2)}</span> },
          {
            chave: 'situacao', rotulo: 'Situação', celula: (p) => (
              <span className="flex flex-col items-start gap-1">
                <SeloDaProposta situacao={p.situacao} />
                {p.noOrcamento.length > 0 ? <span className="txt-apoio text-tinta-apagada">{`No orçamento ${p.noOrcamento.map((o) => `${String(o.exercicio)} v${String(o.versao)}`).join(', ')}`}</span> : null}
              </span>
            ),
          },
        ]}
        linhas={d?.propostas ?? []}
        chaveDaLinha={(p) => p.id}
        carregando={consulta.isPending}
        alturaDupla
        erro={consulta.isError ? <ErroDaLista oQue="As propostas de investimento" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
        vazio={<EstadoVazio icone={FileCheck} titulo="Nenhuma proposta de investimento" descricao={propoe ? 'Proponha o investimento pelo botão do cabeçalho, ou a partir de um estudo concluído.' : 'As propostas aparecem aqui quando alguém com a licença de propor investimento fizer a primeira.'} />}
        acoes={(p) => (
          <span className="flex flex-wrap justify-end gap-1">
            {propoe && (p.situacao === 'proposta' || p.situacao === 'priorizada' || p.situacao === 'adiada') && (d?.criterios.some((k) => k.vigente) ?? false) ? <Botao nivel="fantasma" tamanho="p" onClick={() => { aoAgir({ tipo: 'nota', proposta: p }); }}>Dar nota</Botao> : null}
            {permissoes.includes('investimento.aprovar') && p.podeDecidir ? <Botao nivel="fantasma" tamanho="p" onClick={() => { aoAgir({ tipo: 'decidir', proposta: p }); }}>Decidir proposta</Botao> : null}
            {levaAoOrcamento && p.situacao === 'aprovada' && p.investimento !== null && !p.noOrcamento.some((o) => o.situacao === 'rascunho') ? <Botao nivel="fantasma" tamanho="p" onClick={() => { aoAgir({ tipo: 'orcamento', proposta: p }); }}>Levar ao orçamento</Botao> : null}
          </span>
        )}
        rodape={<RodapeDeContagem total={d?.propostas.length ?? 0} />}
      />
    </>
  );
}

function AbaDosCriterios({ aoAgir }: { readonly aoAgir: (a: Acao) => void }) {
  const sessao = useSessao();
  const aprovaCriterio = (sessao.data?.permissoes ?? []).includes('criterio.aprovar');
  const consulta = useQuery({ queryKey: ['renovacao', 'criterios'], queryFn: apiDaRenovacao.criterios, meta: { oQueLia: 'os critérios de priorização' } });
  const d = consulta.data;
  return (
    <>
      <p className="txt-apoio text-pretty text-tinta-secundaria">{`Só o critério aprovado e vigente entra na nota. Quem propõe o critério não o aprova; aprovar e retirar pedem o perfil ${perfilAPedir('criterio.aprovar')}.`}</p>
      <Tabela
        rotulo="Os critérios de priorização"
        colunas={[
          { chave: 'nome', rotulo: 'Critério', prioridade: 'chave', celula: (c) => <span className="flex min-w-0 flex-col"><span className="txt-corpo text-tinta">{c.nome}</span><TextoDaCelula texto={c.descricao} classe="txt-apoio text-tinta-apagada" linhas={2} /></span> },
          { chave: 'peso', rotulo: 'Peso', alinhamento: 'numero', celula: (c) => <span className="numero text-tinta">{formatarDecimal(c.peso, 1)}</span> },
          { chave: 'vigencia', rotulo: 'Vigência', celula: (c) => <span className="numero text-tinta">{`${formatarDia(c.desde)}${c.ate ? ` a ${formatarDia(c.ate)}` : ' em diante'}`}</span> },
          { chave: 'situacao', rotulo: 'Situação', celula: (c) => <SeloDoOrcamento rotulo={c.situacao === 'aprovado' ? (c.vigente ? 'Aprovado' : 'Fora da vigência') : c.situacao === 'proposto' ? 'Proposto' : 'Retirado'} tom={c.situacao === 'aprovado' ? (c.vigente ? 'sucesso' : 'neutro') : c.situacao === 'proposto' ? 'atencao' : 'neutro'} /> },
          { chave: 'quem', rotulo: 'Quem', prioridade: 'secundaria', celula: (c) => <TextoDaCelula texto={`${c.propostoPor ?? '—'}${c.aprovadoPor ? ` · aprovado por ${c.aprovadoPor}` : ''}`} classe="txt-apoio text-tinta-secundaria" linhas={2} /> },
        ]}
        linhas={d?.criterios ?? []}
        chaveDaLinha={(c) => c.id}
        carregando={consulta.isPending}
        alturaDupla
        erro={consulta.isError ? <ErroDaLista oQue="Os critérios de priorização" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : null}
        vazio={<EstadoVazio icone={Percent} titulo="Nenhum critério de priorização" descricao="Proponha os critérios (risco operacional, retorno, idade da frota) com o peso de cada um: a aprovação é de outra pessoa." />}
        acoes={(c) => (
          <span className="flex flex-wrap justify-end gap-1">
            {aprovaCriterio && c.podeAprovar ? <Botao nivel="fantasma" tamanho="p" onClick={() => { aoAgir({ tipo: 'aprovarCriterio', criterio: c }); }}>Aprovar critério</Botao> : null}
            {aprovaCriterio && c.situacao === 'aprovado' ? <Botao nivel="fantasma" tamanho="p" onClick={() => { aoAgir({ tipo: 'retirarCriterio', criterio: c }); }}>Retirar critério</Botao> : null}
          </span>
        )}
        rodape={<RodapeDeContagem total={d?.criterios.length ?? 0} />}
      />
    </>
  );
}

function Erro({ erro }: { readonly erro: unknown }) {
  const f = explicarFalha(erro);
  return <p role="alert" className="txt-apoio text-parada-texto">{`${f.erro}. ${f.comoResolver}`}</p>;
}

/** Hoje no fuso da operação (`diaDaOperacao`), e não no fuso do aparelho. */
function hojeIso(): string {
  return diaDaOperacao();
}

function JanelaDoCriterio({ aberta, aoFechar }: { readonly aberta: boolean; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [peso, setPeso] = useState('');
  const [desde, setDesde] = useState(hojeIso);
  const [tentou, setTentou] = useState(false);
  const p = interpretarNumero(peso);
  const falta = {
    nome: nome.trim().length < 3 ? 'Dê um nome ao critério, por exemplo "Risco operacional".' : null,
    descricao: descricao.trim().length < 10 ? 'Diga o que o critério mede, com pelo menos 10 caracteres.' : null,
    peso: p === null || p <= 0 || p > 100 ? 'Informe o peso de 1 a 100.' : null,
    desde: /^\d{4}-\d{2}-\d{2}$/.test(desde) ? null : 'Escolha a data de início no calendário.',
  };
  const propor = useMutation({
    mutationFn: () => apiDaRenovacao.proporCriterio({ nome, descricao, peso: p ?? 0, desde, ate: null }),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.proporCriterio>>>((r) => ({ titulo: 'Critério proposto', alvo: { nome: r.nome }, fatos: [`Peso ${formatarDecimal(r.peso, 1)}`, `Desde ${formatarDia(r.desde)}`], efeito: 'Só vale depois de aprovado por outra pessoa', ver: '/investimentos?aba=criterios' })) },
    onSuccess: async () => { aoFechar(); setNome(''); setDescricao(''); setPeso(''); await cliente.invalidateQueries({ queryKey: ['renovacao'] }); },
  });
  return (
    <Janela
      titulo="Propor critério"
      descricao="O critério só entra na nota das propostas depois de aprovado por outra pessoa."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="p"
      acoes={<><Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao><Botao nivel="primario" carregando={propor.isPending} onClick={() => { setTentou(true); if (Object.values(falta).every((x) => x === null)) propor.mutate(); }}>Propor critério</Botao></>}
    >
      <div className="flex flex-col gap-4">
        {propor.isError ? <Erro erro={propor.error} /> : null}
        <CampoTexto rotulo="Nome" valor={nome} aoMudar={setNome} placeholder="Risco operacional" validar={() => (tentou ? falta.nome : null)} maxLength={80} />
        <CampoTexto rotulo="O que mede" valor={descricao} aoMudar={setDescricao} placeholder="Quanto a máquina parada atrasa a obra" validar={() => (tentou ? falta.descricao : null)} maxLength={300} />
        <CampoTexto rotulo="Peso" valor={peso} aoMudar={setPeso} placeholder="3" ajuda="A nota final é a média das notas, pesada por este número." validar={() => (tentou ? falta.peso : null)} maxLength={5} />
        <CampoDeData rotulo="Vale desde" valor={desde} aoMudar={setDesde} erro={tentou ? falta.desde : null} />
      </div>
    </Janela>
  );
}

function JanelaDaAprovacaoDoCriterio({ criterio, aoFechar }: { readonly criterio: Criterio; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [nota, setNota] = useState('');
  const aprovar = useMutation({
    mutationFn: () => apiDaRenovacao.aprovarCriterio(criterio.id, nota),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.aprovarCriterio>>>((r) => ({ titulo: 'Critério aprovado', alvo: { nome: r.nome }, fatos: [`Peso ${formatarDecimal(r.peso, 1)}`, r.aprovadoPor ? `Aprovado por ${r.aprovadoPor}` : null], efeito: `${String(r.vigentes)} ${r.vigentes === 1 ? 'critério vigente' : 'critérios vigentes'} na nota das propostas`, ver: '/investimentos?aba=criterios' })) },
    onSuccess: async () => { aoFechar(); await cliente.invalidateQueries({ queryKey: ['renovacao'] }); },
  });
  return (
    <Janela
      titulo={`Aprovar o critério ${criterio.nome}?`}
      descricao={`Proposto por ${criterio.propostoPor ?? 'outra pessoa'}. Você aprova com a licença de aprovar critérios; quem propõe não aprova. Aprovado, ele não muda: o peso novo é outro critério.`}
      aberta
      aoFechar={aoFechar}
      largura="p"
      acoes={<><Botao nivel="terciario" onClick={aoFechar}>Manter proposto</Botao><Botao nivel="primario" carregando={aprovar.isPending} disabled={nota.trim().length < 15} onClick={() => { aprovar.mutate(); }}>Aprovar critério</Botao></>}
    >
      <div className="flex flex-col gap-4">
        {aprovar.isError ? <Erro erro={aprovar.error} /> : null}
        <CampoDoMotivo rotulo="Nota da aprovação" valor={nota} aoMudar={setNota} exemplo="Aprovado na reunião de diretoria de 07/10/2026" minimo={15} maximo={400} />
      </div>
    </Janela>
  );
}

function JanelaDaRetirada({ criterio, aoFechar }: { readonly criterio: Criterio; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const retirar = useMutation({
    mutationFn: () => apiDaRenovacao.retirarCriterio(criterio.id, motivo),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.retirarCriterio>>>((r) => ({ titulo: 'Critério retirado', tom: 'ressalva', alvo: { nome: r.nome }, fatos: [r.motivo], efeito: 'Ele sai da nota das propostas ainda não decididas', ver: '/investimentos?aba=criterios' })) },
    onSuccess: async () => { aoFechar(); await cliente.invalidateQueries({ queryKey: ['renovacao'] }); },
  });
  return (
    <Janela
      titulo={`Retirar o critério ${criterio.nome}?`}
      descricao="Ele sai da nota das propostas ainda não decididas, e fica na história com o motivo. As decididas não mudam."
      aberta
      aoFechar={aoFechar}
      largura="p"
      acoes={<><Botao nivel="terciario" onClick={aoFechar}>Manter critério</Botao><Botao nivel="destrutivo" carregando={retirar.isPending} disabled={!motivoValido(motivo)} onClick={() => { retirar.mutate(); }}>Retirar critério</Botao></>}
    >
      <div className="flex flex-col gap-4">
        {retirar.isError ? <Erro erro={retirar.error} /> : null}
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo="Substituído pelo critério de risco com peso novo" />
      </div>
    </Janela>
  );
}

function JanelaDaNota({ proposta, aoFechar }: { readonly proposta: Proposta; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const criterios = useQuery({ queryKey: ['renovacao', 'propostas'], queryFn: apiDaRenovacao.propostas, meta: { oQueLia: 'os critérios vigentes' } });
  const vigentes = (criterios.data?.criterios ?? []).filter((k) => k.vigente);
  const [criterio, setCriterio] = useState('');
  const [nota, setNota] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [tentou, setTentou] = useState(false);
  const n = interpretarNumero(nota);
  const falta = { criterio: criterio ? null : 'Escolha o critério.', nota: n === null || n < 0 || n > 10 ? 'Informe a nota de 0 a 10.' : null };
  const dar = useMutation({
    mutationFn: () => apiDaRenovacao.darNota(proposta.id, criterio, n ?? 0, justificativa),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.darNota>>>((r) => ({ titulo: 'Nota dada', alvo: { codigo: codigoDaProposta(r.proposta), nome: r.criterio }, fatos: [`Nota ${formatarDecimal(r.nota, 1)}`, r.antes === null ? null : `Antes ${formatarDecimal(r.antes, 1)}`], efeito: r.pontuacao === null ? 'A nota final sai quando todo critério vigente tiver nota' : `Nota final ${formatarDecimal(r.pontuacao, 2)}`, ver: '/investimentos' })) },
    onSuccess: async () => { aoFechar(); await cliente.invalidateQueries({ queryKey: ['renovacao'] }); },
  });
  return (
    <Janela
      titulo={`Dar nota à ${codigoDaProposta(proposta.numero)}`}
      descricao="A nota nova fica por cima, e a anterior continua na história."
      aberta
      aoFechar={aoFechar}
      largura="p"
      acoes={<><Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao><Botao nivel="primario" carregando={dar.isPending} onClick={() => { setTentou(true); if (!falta.criterio && !falta.nota && justificativa.trim().length >= 10) dar.mutate(); }}>Dar nota</Botao></>}
    >
      <div className="flex flex-col gap-4">
        {dar.isError ? <Erro erro={dar.error} /> : null}
        <div className="flex flex-col gap-1">
          <CampoSelecao rotulo="Critério" valor={criterio} aoMudar={setCriterio} rotuloVazio="Escolha o critério" opcoes={vigentes.map((k) => ({ valor: k.id, rotulo: `${k.nome} · peso ${formatarDecimal(k.peso, 1)}` }))} />
          {tentou && falta.criterio ? <p className="txt-apoio text-parada-texto">{falta.criterio}</p> : null}
        </div>
        <CampoTexto rotulo="Nota (0 a 10)" valor={nota} aoMudar={setNota} placeholder="8" validar={() => (tentou ? falta.nota : null)} maxLength={4} />
        <CampoDoMotivo rotulo="Justificativa" valor={justificativa} aoMudar={setJustificativa} exemplo="A obra norte depende só desta máquina" minimo={10} maximo={300} />
      </div>
    </Janela>
  );
}

const DECISOES: readonly (readonly [Exclude<SituacaoDaProposta, 'proposta'>, string])[] = [
  ['aprovada', 'Aprovar'],
  ['priorizada', 'Priorizar'],
  ['adiada', 'Adiar'],
  ['recusada', 'Recusar'],
];

function JanelaDaDecisao({ proposta, aoFechar }: { readonly proposta: Proposta; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [decisao, setDecisao] = useState<Exclude<SituacaoDaProposta, 'proposta'>>('aprovada');
  const [nota, setNota] = useState('');
  const decidir = useMutation({
    mutationFn: () => apiDaRenovacao.decidir(proposta.id, decisao, nota),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.decidir>>>((r) => ({ titulo: tituloDaDecisao(r.depois), ...detalheDaDecisao(r) })) },
    onSuccess: async () => { aoFechar(); await cliente.invalidateQueries({ queryKey: ['renovacao'] }); },
  });
  return (
    <Janela
      titulo={`Decidir a ${codigoDaProposta(proposta.numero)}`}
      descricao={`${proposta.nomeDoTipo} · ${proposta.codigo ?? proposta.tipoDeMaquina ?? ''} · ${valorDaProposta(proposta)} em ${formatarCompetencia(proposta.dataPretendida)}. Proposta por ${proposta.propostaPor ?? 'outra pessoa'}: você decide com a licença de aprovar investimentos, e quem propõe não decide.`}
      aberta
      aoFechar={aoFechar}
      largura="m"
      acoes={<><Botao nivel="terciario" onClick={aoFechar}>Manter como está</Botao><Botao nivel="primario" carregando={decidir.isPending} disabled={nota.trim().length < 15} onClick={() => { decidir.mutate(); }}>Gravar decisão</Botao></>}
    >
      <div className="flex flex-col gap-4">
        {decidir.isError ? <Erro erro={decidir.error} /> : null}
        <div className="flex flex-col gap-1">
          <span className="txt-rotulo-campo">Risco operacional</span>
          <p className="txt-apoio text-tinta">{`${NOME_DO_RISCO[proposta.risco]}: ${proposta.justificativaDoRisco}`}</p>
          <p className="txt-apoio text-tinta-secundaria">{proposta.pontuacao === null ? `Sem nota final${proposta.semNota.length ? `: falta ${proposta.semNota.join(', ')}` : ''}.` : `Nota final ${formatarDecimal(proposta.pontuacao, 2)}.`}</p>
        </div>
        <CampoSelecao rotulo="Decisão" valor={decisao} aoMudar={(v) => { setDecisao(v as Exclude<SituacaoDaProposta, 'proposta'>); }} opcoes={DECISOES.map(([v, r]) => ({ valor: v, rotulo: r }))} />
        <CampoDoMotivo rotulo="Nota da decisão" valor={nota} aoMudar={setNota} exemplo="Aprovada na reunião de diretoria de 07/10/2026" minimo={15} maximo={400} />
        <p className="txt-apoio flex items-start gap-2 text-tinta-secundaria"><CalendarClock aria-hidden="true" className="mt-0.5 size-4 shrink-0" />Aprovada ou recusada, a proposta não muda. A aprovada vai ao orçamento em rascunho pelo botão da lista.</p>
      </div>
    </Janela>
  );
}

function JanelaDoOrcamento({ proposta, aoFechar }: { readonly proposta: Proposta; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const opcoes = useQuery({ queryKey: ['renovacao', 'opcoes'], queryFn: apiDaRenovacao.opcoes, meta: { oQueLia: 'os centros de custo' } });
  const [centro, setCentro] = useState('');
  const [abrirRevisao, setAbrirRevisao] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const levar = useMutation({
    mutationFn: () => apiDaRenovacao.levarAoOrcamento(proposta.id, centro, abrirRevisao),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.levarAoOrcamento>>>((r) => ({ titulo: 'Proposta levada ao orçamento', ...detalheDoOrcamento(r) })), silenciarFalha: true },
    onSuccess: async () => { aoFechar(); await cliente.invalidateQueries({ queryKey: ['renovacao'] }); await cliente.invalidateQueries({ queryKey: ['orcamento'] }); },
    onError: (erro) => {
      const status = (erro as { status?: number }).status;
      if (status === 409) { setAbrirRevisao(true); setAviso(explicarFalha(erro).comoResolver); }
    },
  });
  const exercicio = proposta.dataPretendida.slice(0, 4);
  return (
    <Janela
      titulo={`Levar a ${codigoDaProposta(proposta.numero)} ao orçamento`}
      descricao={`A proposta vira uma linha de investimento de ${valorDaProposta(proposta)} em ${formatarCompetencia(proposta.dataPretendida)}, na versão em rascunho do orçamento de ${exercicio}. O módulo 20 não escreve no orçamento aprovado.`}
      aberta
      aoFechar={aoFechar}
      largura="p"
      acoes={<><Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao><Botao nivel="primario" carregando={levar.isPending} disabled={!centro} onClick={() => { levar.mutate(); }}>{abrirRevisao ? 'Abrir revisão e levar' : 'Levar ao orçamento'}</Botao></>}
    >
      <div className="flex flex-col gap-4">
        {aviso ? <p role="alert" className="txt-apoio text-atencao-texto">{aviso}</p> : levar.isError ? <Erro erro={levar.error} /> : null}
        <CampoSelecao rotulo="Centro de custo" valor={centro} aoMudar={setCentro} rotuloVazio="Escolha o centro" opcoes={(opcoes.data?.centros ?? []).map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.nome}` }))} />
      </div>
    </Janela>
  );
}
